import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import './History.css';

const History = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [checks, setChecks] = useState([]);
  const [selectedCheck, setSelectedCheck] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadHistory();
  }, []);

  const loadHistory = async () => {
    try {
      const response = await axios.get(`${API}/grammar/history`);
      setChecks(response.data);
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="history-container">
      <div className="glass-background"></div>

      {/* ⭐ NAVBAR (NEW STYLE MATCHING HOME) */}
      <nav className="history-nav fade-in">
        <div className="nav-brand" onClick={() => navigate('/')}>
          <img
            src="/icons/logo-512.png"
            alt="GrammAA Logo"
            className="nav-logo"
          />
          <span className="brand-text">GrammAA</span>
        </div>

        <div className="nav-links">
          <Button variant="ghost" onClick={() => navigate('/dashboard')}>
            Dashboard
          </Button>
          <Button variant="ghost" onClick={() => navigate('/checker')}>
            Checker
          </Button>
        </div>
      </nav>

      {/* ⭐ MAIN CONTENT */}
      <div className="history-content fade-in">
        <div className="history-header">
          <h1>Check History</h1>
          <p className="history-subtitle">
            View all your previous grammar checks
          </p>
        </div>

        {/* ⭐ LOADING */}
        {loading ? (
          <div className="loading-state">
            <div className="loading-spinner"></div>
          </div>
        ) : checks.length > 0 ? (
          /* ⭐ HISTORY GRID */
          <div className="history-grid">
            {checks.map((check, index) => (
              <Card
                key={check.id}
                className="history-card"
                onClick={() => setSelectedCheck(check)}
              >
                <div className="card-header">
                  <Badge className="score-badge">{check.grammar_score}/100</Badge>
                  <span className="card-date">
                    {new Date(check.created_at).toLocaleDateString()}
                  </span>
                </div>

                <p className="card-text">
                  {check.original_text.substring(0, 150)}
                  {check.original_text.length > 150 ? '...' : ''}
                </p>

                <div className="card-footer">
                  <span className="mistakes-count">
                    {check.mistakes?.length || 0} mistakes found
                  </span>
                </div>
              </Card>
            ))}
          </div>
        ) : (
          /* ⭐ NO CHECKS YET */
          <div className="empty-state">
            <p>No grammar checks yet</p>
            <Button onClick={() => navigate('/checker')} className="glass-btn">
              Start Checking
            </Button>
          </div>
        )}
      </div>

      {/* ⭐ DETAIL MODAL */}
      {selectedCheck && (
        <Dialog open={!!selectedCheck} onOpenChange={() => setSelectedCheck(null)}>
          <DialogContent className="detail-dialog">
            <DialogHeader>
              <DialogTitle>Check Details</DialogTitle>
            </DialogHeader>

            <div className="dialog-content">
              {/* ORIGINAL */}
              <div className="detail-section">
                <h3>Original Text</h3>
                <p className="detail-text">{selectedCheck.original_text}</p>
              </div>

              {/* CORRECTED */}
              <div className="detail-section">
                <h3>Corrected Text</h3>
                <p className="detail-text">{selectedCheck.corrected_text}</p>
              </div>

              {/* SCORE */}
              <div className="detail-section">
                <h3>Grammar Score</h3>
                <Badge className="score-badge-large">
                  {selectedCheck.grammar_score}/100
                </Badge>
              </div>

              {/* MISTAKES */}
              <div className="detail-section">
                <h3>Mistakes ({selectedCheck.mistakes?.length || 0})</h3>

                <div className="mistakes-list">
                  {selectedCheck.mistakes?.map((mistake, index) => (
                    <div key={index} className="mistake-item">
                      <Badge className="mistake-type">{mistake.type}</Badge>

                      <p className="mistake-details">
                        <span className="mistake-original">{mistake.original}</span>
                        <span className="arrow">→</span>
                        <span className="mistake-corrected">{mistake.corrected}</span>
                      </p>

                      <p className="mistake-explanation">{mistake.explanation}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default History;
